import { useAuth } from "@features/auth/context/AuthContext";
import PaginationNumeric from "@shared/components/PaginationNumeric";
import { Loader2, RefreshCw, Search } from "lucide-react";
import theme from "@shared/utils/themeColors";
import { useState } from "react";
import { Navigate } from "react-router-dom";
import AuthenticatedLayout from "../../../shared/layouts/AuthenticatedLayout";
import ContactImportModal from "../components/ContactImportModal";
import DeleteUserModal from "../components/DeleteUserModal";
import { InvitationForm } from "../components/InvitationForm";
import { InvitationsList } from "../components/InvitationsList";
import SettingsSidebar from "../components/SettingsSidebar";
import {
  AdminInvitationFilter,
  useAdminInvitations,
} from "../hooks/useAdminInvitations";
import useUserDeletion from "../hooks/useUserDeletion";
import { CreateInvitationRequest, InvitationStatus } from "../types/invitation";

const FILTERS: Array<{ value: AdminInvitationFilter; label: string }> = [
  { value: "all", label: "All" },
  { value: InvitationStatus.PENDING, label: "Pending" },
  { value: InvitationStatus.ACCEPTED, label: "Accepted" },
  { value: InvitationStatus.EXPIRED, label: "Expired" },
];

export default function AdminInvitationsSettings() {
  const { user, isLoading: authLoading } = useAuth();
  const {
    invitations,
    loading,
    createInvitation,
    deleteInvitation,
    resendInvitation,
    copyInvitationLink,
    fetchInvitations,
    search,
    setSearch,
    isSearchPending,
    statusFilter,
    setStatusFilter,
    totalForFilter,
    page,
    setPage,
    pageCount,
    pageSize,
    total,
  } = useAdminInvitations();

  const {
    isLoading: isDeletingUser,
    isModalOpen,
    availableUsers,
    currentInvitation,
    openDeleteModal,
    closeDeleteModal,
    deleteUser,
  } = useUserDeletion();

  const [importModalOpen, setImportModalOpen] = useState(false);

  const handleSubmit = (data: CreateInvitationRequest) => {
    createInvitation(data);
  };

  const handleImportClose = () => {
    setImportModalOpen(false);
    fetchInvitations();
  };

  // Redirect if user is not an admin
  if (!authLoading && (!user || user.role !== "admin")) {
    return <Navigate to="/settings/account" replace />;
  }

  const firstRow = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const lastRow = (page - 1) * pageSize + invitations.length;

  const isFiltering = search.trim() !== "" || statusFilter !== "all";

  return (
    <AuthenticatedLayout>
      <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">
        {/* Page header */}
        <div className="mb-8">
          {/* Title */}
          <h1
            className={`text-2xl md:text-3xl ${theme.text.primary} font-bold`}
          >
            Admin Invitations
          </h1>
        </div>

        {/* Content */}
        <div className={`${theme.bg.primary} shadow-sm rounded-xl mb-8`}>
          <div className="flex flex-col md:flex-row md:-mr-px">
            <SettingsSidebar />
            <div className="flex-grow p-6">
              <div className="mb-8">
                <p className="text-gray-600 mb-6">
                  Send invitations to new organizations administrators. These
                  users will be able to create their own organizations.
                </p>
                <InvitationForm
                  onSubmit={handleSubmit}
                  isLoading={loading}
                  title="Invite Administrators"
                  role="admin"
                  onImportClick={() => setImportModalOpen(true)}
                />
              </div>

              {/* Search and status filter */}
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 mb-4">
                <div className="flex items-center gap-2 w-full lg:max-w-sm">
                  <div className="relative flex-1">
                    <label className="sr-only" htmlFor="invitation-search">
                      Search invitations by email
                    </label>
                    <input
                      id="invitation-search"
                      type="search"
                      className="form-input w-full pl-9"
                      placeholder="Search by email…"
                      value={search}
                      onChange={(event) => setSearch(event.target.value)}
                    />
                    {/* Typing has not reached the server yet: without this, the
                        pause before the results move looks like nothing happened. */}
                    {isSearchPending ? (
                      <Loader2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-gray-400" />
                    ) : (
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={() => fetchInvitations()}
                    disabled={loading}
                    title="Reload the list"
                    aria-label="Reload the list"
                    className="shrink-0 p-2.5 rounded-lg border border-gray-200 dark:border-gray-700/60 text-gray-500 dark:text-gray-400 hover:bg-gray-50 hover:text-gray-700 dark:hover:bg-gray-700/40 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                  </button>
                </div>

                <div className="flex flex-wrap gap-2">
                  {FILTERS.map((filter) => (
                    <button
                      key={filter.value}
                      type="button"
                      onClick={() => setStatusFilter(filter.value)}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                        statusFilter === filter.value
                          ? "bg-gray-800 text-white dark:bg-gray-200 dark:text-gray-900"
                          : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                      }`}
                    >
                      {filter.label}
                      <span className="ml-1.5 opacity-60 tabular-nums">
                        {totalForFilter(filter.value)}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              <InvitationsList
                invitations={invitations}
                onDelete={deleteInvitation}
                onResend={resendInvitation}
                onCopyLink={copyInvitationLink}
                isLoading={loading}
                title="Admin Invitations"
                emptyMessage={
                  isFiltering
                    ? "No invitations match this search."
                    : "No admin invitations have been sent yet."
                }
                defaultRoleLabel="Admin"
              />

              {total > 0 && (
                <div className="mt-6">
                  <PaginationNumeric
                    currentPage={page}
                    totalPages={pageCount}
                    onPageChange={setPage}
                  />
                  <p className="text-center text-sm text-gray-500 dark:text-gray-400 tabular-nums mt-3">
                    Showing {firstRow}–{lastRow} of {total}
                  </p>
                </div>
              )}

              <DeleteUserModal
                isOpen={isModalOpen}
                onClose={closeDeleteModal}
                onConfirm={deleteUser}
                isLoading={isDeletingUser}
                availableUsers={availableUsers}
                deletedInvitation={currentInvitation}
              />

              <ContactImportModal
                isOpen={importModalOpen}
                onClose={handleImportClose}
                role="admin"
                isAdmin={true}
              />
            </div>
          </div>
        </div>
      </div>
    </AuthenticatedLayout>
  );
}
